/**
 * How much of the look sensitivity is left while the sights are up.
 *
 * It writes to [adsLookMultiplier](../../../core/input/adsLookMultiplier.ts), which every control
 * map reads per tick through `adsLookScale`, blended by how far into the sights the body actually
 * is. The weapon's own sighted slow-down still applies on top, so this knob is the player's figure
 * and never the gun's.
 *
 * The bounds and the default are that module's, not this file's, on the terms the sensitivity knob
 * states: a knob names a quantity and hands a value to whoever owns it.
 */

import {
  DEFAULT_ADS_LOOK_MULTIPLIER,
  MAX_ADS_LOOK_MULTIPLIER,
  MIN_ADS_LOOK_MULTIPLIER,
  setAdsLookMultiplier,
} from '@/core/input/adsLookMultiplier';
import { rangeApply } from '@/ui/settings/controls/rangeSlider';
import { SETTING_ADS_LOOK_HINT, SETTING_ADS_LOOK_LABEL } from '@/text/settingsText';
import type { SettingDefinition } from '@/ui/settings/settingContract';

/** Twenty-six stops across the range: fine enough to tune, coarse enough to land on one twice. */
const STEP = 0.05;

export const ADS_LOOK_MULTIPLIER: SettingDefinition = {
  id: 'adsLookMultiplier',
  section: 'controls',
  label: SETTING_ADS_LOOK_LABEL,
  hint: SETTING_ADS_LOOK_HINT,
  control: {
    kind: 'range',
    min: MIN_ADS_LOOK_MULTIPLIER,
    max: MAX_ADS_LOOK_MULTIPLIER,
    step: STEP,
    format: (value: number): string => value.toFixed(2),
  },
  defaultValue: DEFAULT_ADS_LOOK_MULTIPLIER,
  apply: rangeApply(setAdsLookMultiplier),
};
